/**
 * Draws the minecarts (entities/vehicles/minecart.ts): vanilla's MinecartModel, five slabs (floor, four walls)
 * from entity/minecart/minecart.png (64x32), posed from the sim: interpolated position, yaw along the rail,
 * pitch on slopes and the little wobble after a hit.
 */

import * as THREE from 'three';
import type { EnvUniforms } from './env';
import { buildPart, entityMaterial, modelRoot, setEffects, sheetTexture, type EntityMat, type PartDef } from './entities/core';
import type { Minecart } from '../entities/vehicles/minecart';

const H = Math.PI / 2;
const WALL = { tex: [0, 0], from: [-8, -9, -1], size: [16, 8, 2] } as const;
/** Vanilla MinecartModel parts, in model pixels. */
export const MINECART_PARTS: readonly PartDef[] = [
  { cubes: [{ tex: [0, 10], from: [-10, -8, -1], size: [20, 16, 2] }], pos: [0, 4, 0], rot: [H, 0, 0] },
  { cubes: [WALL], pos: [-9, 4, 0], rot: [0, 3 * H, 0] },
  { cubes: [WALL], pos: [9, 4, 0], rot: [0, H, 0] },
  { cubes: [WALL], pos: [0, 4, -7], rot: [0, Math.PI, 0] },
  { cubes: [WALL], pos: [0, 4, 7], rot: [0, 0, 0] },
];

interface View { yawG: THREE.Group; pitchG: THREE.Group; mat: EntityMat }

const lerpAngle = (a: number, b: number, t: number): number => {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2; else if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
};

export class MinecartRenderer {
  readonly group = new THREE.Group();
  private readonly views = new Map<Minecart, View>();
  private readonly tex = sheetTexture('entity/minecart/minecart');

  constructor(private readonly env: EnvUniforms) {}

  /** Build one cart's meshes (also used by tests / contact sheets). */
  private build(): View {
    const mat = entityMaterial(this.env, this.tex); // one per cart: light and hurt flash differ
    const yawG = new THREE.Group();
    const pitchG = new THREE.Group();
    const root = modelRoot();
    root.position.y = 0.375;
    for (const p of MINECART_PARTS) root.add(buildPart(p, 64, 32, mat));
    pitchG.add(root);
    yawG.add(pitchG);
    this.group.add(yawG);
    return { yawG, pitchG, mat };
  }

  /** Pose the carts of `dim` for a frame `alpha` (0..1) into the current tick. */
  update(carts: readonly Minecart[], alpha: number, dim: string): void {
    const live = new Set<Minecart>();
    for (const c of carts) {
      if (c.removed || c.dim !== dim) continue;
      live.add(c);
      let v = this.views.get(c);
      if (!v) { v = this.build(); this.views.set(c, v); }
      v.yawG.position.set(c.px + (c.x - c.px) * alpha, c.py + (c.y - c.py) * alpha, c.pz + (c.z - c.pz) * alpha);
      // the model's long side is its x axis: turn it to the heading
      v.yawG.rotation.y = -lerpAngle(c.pyaw, c.yaw, alpha);
      v.pitchG.rotation.z = c.ppitch + (c.pitch - c.ppitch) * alpha;
      v.pitchG.rotation.x = c.hurtTime > 0 ? Math.sin(c.hurtTime) * c.hurtTime * c.damage * 0.002 : 0;
      setEffects([v.mat], 1, c.hurtTime > 0, 0, { x: c.x, y: c.y + 0.5, z: c.z });
    }
    for (const [c, v] of this.views) {
      if (live.has(c)) continue;
      this.group.remove(v.yawG);
      this.views.delete(c);
    }
  }
}
